
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import AnimateInView from '@/components/ui/animate-in-view';

/**
 * @component FAQSection
 * @description Frequently asked questions section with accordion functionality
 * @returns {JSX.Element} FAQ section with common security questions
 */
const FAQSection = () => {
  const faqs = [
    {
      question: "How does Forti-Fi's technology differ from traditional security systems?",
      answer: "Unlike traditional security systems that rely on visible cameras and motion sensors, Forti-Fi's technology is completely invisible. Our fiber optic cables detect intrusions through minute changes in light patterns, while our on-fence solutions integrate seamlessly with existing structures. This provides superior detection capabilities without alerting potential intruders to the presence of security measures."
    },
    {
      question: "How quickly can a Forti-Fi system be installed?",
      answer: "Installation timelines vary based on property size and system complexity. For standard residential estates, our installation teams typically complete setup within 1-2 weeks. For larger commercial properties or estates with complex requirements, installation may take 2-4 weeks. Every installation includes comprehensive testing and calibration to ensure optimal performance."
    },
    {
      question: "Are Forti-Fi systems suitable for historic properties?",
      answer: "Absolutely. Our systems are designed with preservation in mind and can be installed with minimal disruption to historic structures. The invisible nature of our fiber optic technology makes it particularly well-suited for heritage properties where preservation of aesthetic and structural integrity is paramount."
    },
    {
      question: "How does maintenance work for an underground security system?",
      answer: "Our systems require minimal maintenance due to their robust design. Fiber optic cables are housed in protective conduits and are resistant to environmental factors. We provide quarterly remote diagnostics and annual on-site inspections as part of our standard service agreements. Our systems include self-diagnostic capabilities that alert our monitoring center to any potential issues."
    },
    {
      question: "Can Forti-Fi security systems integrate with my existing smart home setup?",
      answer: "Yes, our systems are designed with integration in mind. We offer seamless connectivity with major smart home platforms, allowing for unified control and monitoring. Our API enables custom integration with proprietary systems often found in high-end properties."
    }
  ];

  return (
    <section className="py-16 bg-white" aria-labelledby="faq-heading">
      <div className="container mx-auto px-4">
        <AnimateInView animation="fade-in" className="block">
          <div className="text-center mb-12">
            <div className="h-0.5 bg-gold w-20 mx-auto mb-4"></div>
            <h2 id="faq-heading" className="text-3xl font-serif text-forti-fi mb-4">
              Frequently Asked Questions
            </h2>
          </div>
        </AnimateInView>
        
        <div className="max-w-3xl mx-auto">
          <AnimateInView animation="fade-in" delay={100} className="block">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="border border-forti-fi-lavender/30 rounded-md px-4 shadow-sm transition-all duration-300 hover:shadow-md"
                >
                  <AccordionTrigger className="text-forti-fi-charcoal hover:text-forti-fi font-medium py-4 transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-forti-fi-charcoal/80 pb-4 pt-2 animate-fade-in">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimateInView>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
